import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';

import {
	Button,
	Empty,
	Group,
	Pagination,
	SkeletonRect,
	Text,
	toast,
} from '@ekhodealer/ekho-common/rev';

import type { TableColumn } from '@common/components/Table/Table';
import type { LeadsTableRow } from '@ekhodealer/ekho-common';

import { FloatingActionMenu } from '@common/components/FloatingActionMenu';
import NoLeads from '@common/components/icons/NoLeads';
import { SelectAllBanner } from '@common/components/SelectAllBanner';
import { Table } from '@common/components/Table/Table';

import { useExportLeads } from '../api/useExportLeads';
import { useSearchLeads } from '../api/useSearchLeads';
import { activeFiltersCountAtom } from '../atoms/leadsAppliedFiltersAtoms';
import {
	clearAllFiltersAtom,
	currentPageAtom,
	isAllMatchingLeadsSelectedAtom,
	leadsParamsAtom,
	selectedLeadsAtom,
	updateItemsPerPageAtom,
	updateSelectedLeadsAtom,
} from '../atoms/leadsAtoms';
import { LeadsTableColumnId } from '../constants/leadsTableColumns';
import { useAccountNameMap } from '../hooks/useAccountNameMap';
import { useLeadNavigation } from '../hooks/useLeadNavigation';
import { currentLeadsListAtom } from '../hooks/useLeadNavigation';

import { LeadActionMenu } from './LeadActionMenu';
import { LeadDetailsDrawer } from './LeadDetailsDrawer';
import {
	AccountNameCell,
	DateCreatedCell,
	DealAmountCell,
	EmailCell,
	LastUpdatedCell,
	NameCell,
	PhoneCell,
	PipelineCell,
	PrequalCell,
	StageCell,
	TasksCell,
	TypeCell,
	VehicleInterestsCell,
} from './table-cells';

// Centralized cell rendering function
const renderCell = (
	columnId: LeadsTableColumnId,
	item: LeadsTableRow,
	isLoading: boolean,
): React.ReactNode => {
	if (isLoading) {
		return <SkeletonRect width={100} height={24} />;
	}

	switch (columnId) {
		case LeadsTableColumnId.NAME:
			return <NameCell name={item.name} />;
		case LeadsTableColumnId.PREQUAL_STATUS:
			return <PrequalCell prequalStatus={item.preQualStatus} />;
		case LeadsTableColumnId.EMAIL:
			return <EmailCell email={item.email} />;
		case LeadsTableColumnId.PHONE:
			return <PhoneCell phone={item.phone} />;
		case LeadsTableColumnId.PIPELINE:
			return <PipelineCell pipeline={item.pipeline} />;
		case LeadsTableColumnId.STAGE:
			return <StageCell item={item} />;
		case LeadsTableColumnId.TYPE:
			return <TypeCell type={item.type} />;
		case LeadsTableColumnId.VEHICLE_INTERESTS:
			return <VehicleInterestsCell vehicleInterests={item.vehicleInterests} />;
		case LeadsTableColumnId.DEAL_AMOUNT:
			return <DealAmountCell dealAmount={item.dealAmount} />;
		case LeadsTableColumnId.DATE_CREATED:
			return <DateCreatedCell dateCreated={item.dateCreated} />;
		case LeadsTableColumnId.LAST_UPDATED:
			return <LastUpdatedCell lastUpdated={item.lastUpdated} />;
		case LeadsTableColumnId.TASKS:
			return <TasksCell item={item} />;
		case LeadsTableColumnId.ACTIONS:
			return <LeadActionMenu item={item} />;
		default:
			return <Text size='bodySmall'>—</Text>;
	}
};

/**
 * LeadsTable component
 * Displays leads in a table format with sorting and pagination
 * Follows the same patterns as OrdersTable for consistency
 */
export const LeadsTable = () => {
	// Get filter params from atoms
	const params = useAtomValue(leadsParamsAtom);

	// Pagination state
	const [currentPage, setCurrentPage] = useAtom(currentPageAtom);
	const [itemsPerPage, setItemsPerPage] = useAtom(updateItemsPerPageAtom);

	// Selection state
	const selectedLeads = useAtomValue(selectedLeadsAtom);
	const updateSelectedLeads = useSetAtom(updateSelectedLeadsAtom);
	const [isAllMatchingSelected, setIsAllMatchingSelected] = useAtom(
		isAllMatchingLeadsSelectedAtom,
	);

	// Navigation
	const { openLeadDetails } = useLeadNavigation();

	// Map svpId to account names for display
	const accountNameMap = useAccountNameMap();

	// Clear all filters
	const clearAllFilters = useSetAtom(clearAllFiltersAtom);
	const activeFiltersCount = useAtomValue(activeFiltersCountAtom);
	const hasFiltersApplied = activeFiltersCount > 0;

	// React query hook to get leads data with filters
	const { data, isLoading } = useSearchLeads(params);
	const items = useMemo(() => data?.data ?? [], [data?.data]);
	const queryCount = data?.count ?? 0;

	const isAllPageSelected = selectedLeads === 'all';
	const selectedLeadsCount = isAllMatchingSelected
		? queryCount
		: isAllPageSelected
			? items.length
			: (selectedLeads as Set<string>).size;

	// Handle exporting
	const [loadingExport, setLoadingExport] = useState(false);
	const exportLeadsMutation = useExportLeads();

	// Sync current leads list for navigation
	const updateCurrentLeadsList = useSetAtom(currentLeadsListAtom);
	useEffect(() => {
		if (!isLoading && items.length > 0) {
			updateCurrentLeadsList(items.map((item) => item.id));
		}
	}, [items, isLoading, updateCurrentLeadsList]);

	// Define columns
	const columns: TableColumn<LeadsTableRow>[] = [
		{
			id: LeadsTableColumnId.NAME,
			title: 'Name',
			sortable: false, // TODO: Add sorting support
			renderCell: (item) => renderCell(LeadsTableColumnId.NAME, item, isLoading),
		},
		// CXP-2321: Pre-qual column hidden until prequal is functional
		// {
		// 	id: LeadsTableColumnId.PREQUAL_STATUS,
		// 	title: 'Pre-qual',
		// 	sortable: false,
		// 	renderCell: (item) => renderCell(LeadsTableColumnId.PREQUAL_STATUS, item, isLoading),
		// },
		{
			id: LeadsTableColumnId.EMAIL,
			title: 'Email',
			sortable: false,
			renderCell: (item) => renderCell(LeadsTableColumnId.EMAIL, item, isLoading),
		},
		{
			id: LeadsTableColumnId.PHONE,
			title: 'Phone number',
			sortable: false,
			renderCell: (item) => renderCell(LeadsTableColumnId.PHONE, item, isLoading),
		},
		{
			id: LeadsTableColumnId.PIPELINE,
			title: 'Pipeline',
			sortable: false,
			renderCell: (item) => renderCell(LeadsTableColumnId.PIPELINE, item, isLoading),
		},
		{
			id: LeadsTableColumnId.STAGE,
			title: 'Stage',
			sortable: false,
			isButtonCell: true,
			renderCell: (item) => renderCell(LeadsTableColumnId.STAGE, item, isLoading),
		},
		{
			id: LeadsTableColumnId.TYPE,
			title: 'Type',
			sortable: false,
			renderCell: (item) => renderCell(LeadsTableColumnId.TYPE, item, isLoading),
		},
		{
			id: LeadsTableColumnId.VEHICLE_INTERESTS,
			title: 'Vehicle interests',
			sortable: false,
			renderCell: (item) => renderCell(LeadsTableColumnId.VEHICLE_INTERESTS, item, isLoading),
		},
		{
			id: LeadsTableColumnId.DEAL_AMOUNT,
			title: 'Deal amount',
			sortable: false,
			renderCell: (item) => renderCell(LeadsTableColumnId.DEAL_AMOUNT, item, isLoading),
		},
		{
			id: LeadsTableColumnId.ACCOUNT_NAME,
			title: 'Account name',
			sortable: false,
			renderCell: (item) => {
				if (isLoading) return <SkeletonRect width={100} height={24} />;
				const accountName = item.svpId
					? (accountNameMap.get(item.svpId) ?? item.svpId)
					: item.dealerName;
				return <AccountNameCell accountName={accountName} />;
			},
		},
		{
			id: LeadsTableColumnId.DATE_CREATED,
			title: 'Date created',
			sortable: false,
			renderCell: (item) => renderCell(LeadsTableColumnId.DATE_CREATED, item, isLoading),
		},
		{
			id: LeadsTableColumnId.LAST_UPDATED,
			title: 'Last updated',
			sortable: false,
			renderCell: (item) => renderCell(LeadsTableColumnId.LAST_UPDATED, item, isLoading),
		},
		{
			id: LeadsTableColumnId.TASKS,
			title: 'Tasks',
			sortable: false,
			renderCell: (item) => renderCell(LeadsTableColumnId.TASKS, item, isLoading),
		},
		{
			id: LeadsTableColumnId.ACTIONS,
			title: '',
			sortable: false,
			renderCell: (item) => renderCell(LeadsTableColumnId.ACTIONS, item, isLoading),
			renderHeader: () => null, // No header content
		},
	];

	async function handleExportSelectedAsCsv() {
		setLoadingExport(true);

		let leadIds: string[] | undefined;

		if (isAllMatchingSelected) {
			leadIds = undefined;
		} else if (selectedLeads === 'all') {
			leadIds = items.map((item) => item.id);
		} else if (selectedLeads instanceof Set) {
			leadIds = Array.from(selectedLeads).map((id) => String(id));
		}

		try {
			await exportLeadsMutation.mutateAsync({
				leadIds,
			});

			// Show success toast
			toast({
				message: `${selectedLeadsCount} leads exported`,
				variant: 'success',
			});
		} catch {
			// Error is handled by the mutation, but we can still show an error toast
			toast({
				message: 'Failed to export leads. Please try again.',
				variant: 'error',
			});
		}

		setLoadingExport(false);
	}

	// State handlers
	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	const handleItemsPerPageChange = (newItemsPerPage: number) => {
		setItemsPerPage(newItemsPerPage);
	};
	const handleRowActivate = (item: LeadsTableRow) => {
		openLeadDetails(item.id);
	};

	// Placeholder for skeleton items when loading
	const getDisplayItems = () => {
		if (isLoading) {
			return Array.from({ length: itemsPerPage }, (_, index) => ({
				id: `skeleton-${index}`,
				name: '',
				preQualStatus: undefined,
				email: '',
				phone: '',
				pipeline: {
					type: '',
					name: '',
				},
				stage: {
					type: '',
					name: '',
				},
				type: '',
				vehicleInterests: {
					summaryLabel: '',
					models: [],
				},
				dealAmount: '',
				dealerName: '',
				svpId: '',
				dateCreated: '',
				lastUpdated: '',
				numberOfTasks: 0,
			})) as unknown as LeadsTableRow[];
		}

		return items;
	};

	// Check if we have empty results (not loading, no data)
	const hasEmptyResults = !isLoading && items.length === 0;

	// Show empty state when there are no results
	if (hasEmptyResults) {
		return (
			<Empty
				icon={<NoLeads />}
				title={hasFiltersApplied ? 'No leads match your filters' : 'No leads yet'}
				description={
					hasFiltersApplied
						? 'Try adjusting or clearing your filters to see more results.'
						: 'When new leads come in, they’ll appear here. Track interest, follow up faster, and convert opportunities into orders—all in one place.'
				}
				content={
					hasFiltersApplied ? (
						<Button variant='secondary' size='small' onPress={() => clearAllFilters()}>
							Clear all
						</Button>
					) : undefined
				}
			/>
		);
	}

	return (
		<>
			<Table<LeadsTableRow>
				columns={columns}
				items={getDisplayItems()}
				getItemId={(item) => item.id}
				selectionMode='multiple'
				selectedItems={selectedLeads}
				onSelectionChange={updateSelectedLeads}
				onRowActivate={handleRowActivate}
				aria-label={isLoading ? 'Loading leads table' : 'Leads table'}
				stickyRightCount={1}
				belowTableContent={
					!hasEmptyResults &&
					!isLoading && (
						<Pagination
							variant='contained'
							currentPage={currentPage}
							itemsPerPage={itemsPerPage}
							totalItems={queryCount}
							itemsPerPageOptions={[10, 25, 50, 100]}
							onNavigateTo={handlePageChange}
							onItemsPerPageChange={handleItemsPerPageChange}
							aria-label='Leads table pagination'
						/>
					)
				}
			/>
			<FloatingActionMenu
				isOpen={selectedLeadsCount > 0}
				onClose={() => {
					updateSelectedLeads(new Set());
				}}
				closeButtonTooltip='Deselect all'
				leftContent={
					<div style={{ marginRight: 'auto' }}>
						<SelectAllBanner
							selectedCount={
								isAllPageSelected
									? items.length
									: (selectedLeads as Set<string>).size
							}
							totalCount={queryCount}
							isAllPageSelected={isAllPageSelected}
							isAllMatchingSelected={isAllMatchingSelected}
							onSelectAllMatching={() => setIsAllMatchingSelected(true)}
							onClearSelection={() => updateSelectedLeads(new Set())}
							resourceName='leads'
						/>
					</div>
				}
				rightContent={
					<Group noWrap itemsSpacing='8'>
						<Button
							size='medium'
							variant='secondary'
							rounding='10'
							isLoading={loadingExport}
							onPress={handleExportSelectedAsCsv}>
							Export
						</Button>
					</Group>
				}
			/>
			<LeadDetailsDrawer />
		</>
	);
};
